import React, {memo, useEffect, useState} from 'react';
import ReactPaginate from 'react-paginate';
import {StyledPaginationWrapper} from './styled';
import {
  numberToTwoDigitsString,
  scrollToDocumentTop,
} from '../../utils/general';
import ArrowLeft from '../../components/icons/arrow-left';
import ArrowRight from '../../components/icons/arrow-right';
import {LoaderSpinnerModal} from '../loader-spinner';
import {useTrackingManager} from '../../hooks/use-tracking-manager';
import {onVWBasicLinkClickEvent} from '../../tagging/tagging-helpers';

interface PaginationProps {
  readonly totalPages: number;
  readonly currentPage: number;
  readonly setCurrentPage: (pageNumber: number) => void;
}

export const Pagination: React.FC<PaginationProps> = memo(
  (props: PaginationProps): JSX.Element => {
    const {totalPages, currentPage, setCurrentPage} = props;
    const [loading, setLoading] = useState(false);
    const trackingManager = useTrackingManager();

    let pageRangeDisplayed = 3;

    // Workaround for unexpected behavior in last pages
    if (currentPage == totalPages - 1 || currentPage == totalPages - 2) {
      pageRangeDisplayed = 4;
    }

    const handlePageClick = ({selected}) => {
      setLoading(true);
      // Workaround for scenarios when it is reading directly from
      // client cache, this way the user 'knows something is happening'
      // as it will show/hide the loading spinner
      setTimeout(() => {
        setLoading(false);
      }, 500);

      setCurrentPage(selected);
      scrollToDocumentTop();

      // Tagging section
      trackingManager &&
        onVWBasicLinkClickEvent(trackingManager, 'Results Pagination', {
          url: `go to ${selected + 1} page`,
          name: 'click',
          newTab: false,
        });
    };

    const handleLableBuilder = (page) => numberToTwoDigitsString(page);

    const overrideFirstLastPages = () => {
      if (!!!totalPages) return;
      const paginationKey = 'pagination';
      const paginationContainer =
        document.getElementsByClassName(paginationKey)[0];

      const paginationButtons = paginationContainer?.children;
      const breakLeftIndex = 2; // Index of the 'break/ellipsis' button on the left
      const breakRightIndex = paginationButtons
        ? paginationButtons?.length - 3
        : 0; // Index of the 'break/ellipsis' button on the right

      // Update first button text (left side)
      updateButtonContent(paginationButtons, breakLeftIndex, 1, true);
      // Update last button text (right side)
      updateButtonContent(
        paginationButtons,
        breakRightIndex,
        totalPages,
        false,
      );
    };

    const updateButtonContent = (
      htmlCollection: HTMLCollection | undefined,
      breakIndex: number,
      defaultValue: number,
      isLeftBreak: boolean,
    ) => {
      if (!htmlCollection) return;

      const breakKey = 'break';
      const elementOffset = isLeftBreak ? breakIndex - 1 : breakIndex + 1;
      let overrideLabel = htmlCollection
        ? htmlCollection[breakIndex]?.classList.contains(breakKey)
        : undefined;

      if (htmlCollection) {
        const pageNumber = numberToTwoDigitsString(defaultValue);
        const newValue = overrideLabel
          ? isLeftBreak
            ? `${pageNumber}...`
            : `...${pageNumber}`
          : pageNumber;
        let targetPageButton: any;
        htmlCollection && (targetPageButton = htmlCollection[elementOffset]);
        targetPageButton &&
          (targetPageButton.firstChild.textContent = newValue);
      }
    };

    useEffect(overrideFirstLastPages);
    return (
      <>
        <StyledPaginationWrapper>
          <ReactPaginate
            previousLabel={<ArrowLeft iconSize={24} />}
            nextLabel={<ArrowRight iconSize={24} />}
            pageCount={totalPages}
            onPageChange={handlePageClick}
            containerClassName={'pagination'}
            previousLinkClassName={'pagination__link--previous'}
            nextLinkClassName={'pagination__link--next'}
            disabledClassName={'pagination__link--disabled'}
            activeClassName={'pagination__link--active'}
            marginPagesDisplayed={1}
            pageRangeDisplayed={pageRangeDisplayed}
            forcePage={currentPage}
            pageLabelBuilder={handleLableBuilder}
            renderOnZeroPageCount={null}
          />
        </StyledPaginationWrapper>
        {loading ? <LoaderSpinnerModal /> : undefined}
      </>
    );
  },
);
